/**
 * Listing page features: search bar, search dialog, card click-through, country detection
 */
import { getFlagSpritePath, getDefaultIso, addDropdownSearch } from '../utils/helpers.js';
import { openDialog } from './dialogs.js';

export function initListingSearch() {
    const wrappers = document.querySelectorAll(
        '.listing-search-form .listing-search-country, .home-search-form .home-search-country'
    );
    if (!wrappers.length) return;
    wrappers.forEach(initListingSearchDropdown);
}

export function initSearchDialog() {
    const dialog = document.getElementById('search-dialog');
    if (!dialog) return;

    const searchButtons = [...document.querySelectorAll('.search-btn')];
    searchButtons.forEach(searchBtn => {
        searchBtn.addEventListener('click', () => {
            openDialog(dialog, {
                autoFocus: false,
                onOpen: () => {
                    dialog.scrollTop = 0;
                    dialog.querySelector('.dialog-content')?.scrollTo({ top: 0 });

                    if (window.innerWidth <= 700) {
                        return;
                    }

                    const input = dialog.querySelector('input[type="text"]');
                    if (!input) {
                        return;
                    }

                    try {
                        input.focus({ preventScroll: true });
                    } catch {
                        input.focus();
                    }
                }
            });
        });
    });

    // Init the country dropdown inside the dialog
    const wrapper = dialog.querySelector('.listing-search-country--dialog');
    if (wrapper) initListingSearchDropdown(wrapper);
}

export function initListingCardLinks() {
    document.querySelectorAll('.office-card--listing[data-href]').forEach(card => {
        card.addEventListener('click', e => {
            if (e.target.closest('.listing-shortlist-btn')) return;
            if (e.target.closest('[data-map-link]')) return;
            window.location.href = card.dataset.href;
        });
    });

    // Handle broken images — hide them so the CSS placeholder background shows through
    document.querySelectorAll('.office-card-media img.office-card-image').forEach(img => {
        img.addEventListener('error', () => img.classList.add('is-broken'));
        // Also catch images that already failed before JS ran
        if (img.complete && img.naturalWidth === 0) img.classList.add('is-broken');
    });
}

function initListingSearchDropdown(wrapper) {
    const select = wrapper.querySelector('select');
    if (!select) return;

    wrapper.querySelector('.country-select-trigger')?.remove();
    wrapper.querySelector('.country-select-menu')?.remove();

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'country-select-trigger';

    const menu = document.createElement('div');
    menu.className = 'country-select-menu';
    menu.hidden = true;

    // Build priority ISOs: user's detected country + entry's page country
    var userIso = '';
    try { userIso = (localStorage.getItem('newofficeplease_country_code') || '').trim().toLowerCase(); } catch(e) {}
    var entryIso = (document.body.getAttribute('data-entry-country') || '').trim().toLowerCase();
    var priorityIsos = [];
    if (userIso) priorityIsos.push(userIso);
    if (entryIso && entryIso !== userIso) priorityIsos.push(entryIso);

    // Separate priority options from the rest
    var allOptions = [...select.options];
    var priorityOptions = [];
    var restOptions = [];

    allOptions.forEach(function(option) {
        var iso = (option.dataset.countryCode || '').toLowerCase();
        if (priorityIsos.indexOf(iso) !== -1) {
            priorityOptions.push({ option: option, iso: iso, sortOrder: priorityIsos.indexOf(iso) });
        } else {
            restOptions.push(option);
        }
    });

    // Sort priority by the order defined (user first, entry second)
    priorityOptions.sort(function(a, b) { return a.sortOrder - b.sortOrder; });

    function createOptionItem(option, iso) {
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'country-select-option';
        item.dataset.value = option.value;
        item.dataset.iso = iso;
        item.innerHTML = `
            <svg class="country-select-flag" aria-hidden="true"><use href="${getFlagSpritePath()}#cig--flags--${iso}"></use></svg>
            <span class="country-select-label">${option.textContent}</span>
        `;
        item.addEventListener('click', function() {
            select.value = option.value;
            updateListingSearchTrigger(trigger, iso);
            syncCountryChange(iso, option.textContent.trim(), wrapper);
            closeMenu();
            var locationInput = wrapper.closest('form')?.querySelector('input[name="location"]');
            (locationInput || trigger).focus();
        });
        return item;
    }

    // Add priority options at the top (bold to distinguish)
    priorityOptions.forEach(function(p) {
        var item = createOptionItem(p.option, p.iso);
        item.querySelector('.country-select-label').style.fontWeight = '600';
        menu.appendChild(item);
    });

    // Add separator if we have priority items
    if (priorityOptions.length > 0 && restOptions.length > 0) {
        var sep = document.createElement('div');
        sep.className = 'country-select-separator';
        menu.appendChild(sep);
    }

    // Add remaining options
    restOptions.forEach(function(option) {
        var iso = option.dataset.countryCode || '';
        menu.appendChild(createOptionItem(option, iso));
    });

    const closeMenu = ({ resetSearch = true, returnFocus = false } = {}) => {
        wrapper.classList.remove('is-open');
        menu.hidden = true;

        if (resetSearch) {
            search.reset();
        } else {
            search.clearActive();
        }

        if (returnFocus) {
            trigger.focus();
        }
    };

    const setActiveToSelection = () => {
        const selectedItem = [...menu.querySelectorAll('.country-select-option')]
            .find(item => item.dataset.value === select.value);
        search.setActiveOption(selectedItem || search.getVisibleOptions()[0] || null);
    };

    // Add search filter
    const search = addDropdownSearch(menu, {
        placeholder: 'Search countries...',
        onClose: () => closeMenu({ returnFocus: true }),
    });

    trigger.addEventListener('click', e => {
        e.preventDefault();
        const opening = menu.hidden;
        if (!opening) {
            closeMenu();
            return;
        }

        wrapper.classList.add('is-open');
        menu.hidden = false;
        search.reset();
        setActiveToSelection();
        search.focus();
    });

    trigger.addEventListener('keydown', e => {
        if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
            return;
        }

        e.preventDefault();

        if (menu.hidden) {
            wrapper.classList.add('is-open');
            menu.hidden = false;
            search.reset();
            setActiveToSelection();
            if (e.key === 'ArrowUp') {
                const visibleOptions = search.getVisibleOptions();
                search.setActiveOption(visibleOptions[visibleOptions.length - 1] || null);
            }
            search.focus();
            return;
        }

        closeMenu();
    });

    // Remove previous handler if widget was re-initialised
    if (wrapper._dropdownCloseHandler) {
        document.removeEventListener('click', wrapper._dropdownCloseHandler);
    }
    wrapper._dropdownCloseHandler = e => {
        if (!wrapper.contains(e.target)) {
            closeMenu();
        }
    };
    document.addEventListener('click', wrapper._dropdownCloseHandler);

    wrapper.appendChild(trigger);
    wrapper.appendChild(menu);

    const initialIso = select.options[select.selectedIndex]?.dataset.countryCode || getDefaultIso();
    updateListingSearchTrigger(trigger, initialIso);
}

function updateListingSearchTrigger(trigger, iso) {
    trigger.innerHTML = `
        <svg class="country-select-flag" aria-hidden="true"><use href="${getFlagSpritePath()}#cig--flags--${iso}"></use></svg>
    `;
}

// --- Country detection & sync (additions for geolocation) ---

function syncCountryChange(isoCode, countryName, sourceWrapper) {
    var code = isoCode.toLowerCase();

    // Update all OTHER country dropdowns (not the one just clicked)
    document.querySelectorAll('.home-search-country, .listing-search-country').forEach(function(wrapper) {
        if (wrapper === sourceWrapper) return;
        var select = wrapper.querySelector('select');
        if (!select) return;

        var option = Array.from(select.options).find(function(o) {
            return (o.dataset.countryCode || '').toLowerCase() === code;
        });
        if (option) {
            select.value = option.value;
            var trigger = wrapper.querySelector('.country-select-trigger');
            if (trigger) updateListingSearchTrigger(trigger, option.dataset.countryCode || code);
        }
    });

    // Update all search titles
    document.querySelectorAll('.home-hero-title').forEach(function(el) {
        el.textContent = countryName;
    });

    // Save to localStorage
    try { localStorage.setItem('newofficeplease_country_code', code); } catch(e) {}
}

function setCountryByCode(code) {
    code = code.toLowerCase();
    var anySelect = document.querySelector('.js-country-select');
    if (!anySelect) return;

    var option = Array.from(anySelect.options).find(function(o) {
        return (o.dataset.countryCode || '').toLowerCase() === code;
    });
    if (!option) return;

    var countryName = option.textContent.trim();

    // Update ALL country dropdowns
    document.querySelectorAll('.home-search-country, .listing-search-country').forEach(function(wrapper) {
        var select = wrapper.querySelector('select');
        if (!select) return;

        var opt = Array.from(select.options).find(function(o) {
            return (o.dataset.countryCode || '').toLowerCase() === code;
        });
        if (opt) {
            select.value = opt.value;
            var trigger = wrapper.querySelector('.country-select-trigger');
            if (trigger) updateListingSearchTrigger(trigger, opt.dataset.countryCode || code);
        }
    });

    // Update titles
    document.querySelectorAll('.home-hero-title').forEach(function(el) {
        el.textContent = countryName;
    });

    try { localStorage.setItem('newofficeplease_country_code', code); } catch(e) {}
}

export function initListingSchema() {
    if (document.body.id !== 'listing') return;

    const cards = document.querySelectorAll('.office-card--listing[data-href][data-office-name]');
    if (!cards.length) return;

    const items = [];
    cards.forEach((card, i) => {
        items.push({
            '@type': 'ListItem',
            position: i + 1,
            url: window.location.origin + card.dataset.href,
            name: card.dataset.officeName,
        });
    });

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: document.querySelector('.listing-page-title')?.textContent?.trim() || 'Serviced Offices',
        url: window.location.href.split('?')[0],
        numberOfItems: items.length,
        itemListOrder: 'https://schema.org/ItemListUnordered',
        itemListElement: items,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
}

/**
 * Run the country detection chain.
 * Checks URL param → localStorage → cookie → API (in that order).
 * Returns true if resolved without API, false if API was needed.
 */
var countryDetectionDone = false;

function detectCountry() {
    if (countryDetectionDone) return;

    // Priority 1: URL country-id parameter
    var urlCountryId = new URLSearchParams(location.search).get('country-id');
    if (urlCountryId) {
        var anySelect = document.querySelector('.js-country-select');
        if (anySelect) {
            var option = Array.from(anySelect.options).find(function(o) {
                return o.value === urlCountryId;
            });
            if (option) {
                var code = (option.dataset.countryCode || '').toLowerCase();
                if (code) {
                    setCountryByCode(code);
                    countryDetectionDone = true;
                    return;
                }
            }
        }
    }

    // Priority 2: Saved preference from localStorage
    var savedCode = null;
    try { savedCode = localStorage.getItem('newofficeplease_country_code'); } catch(e) {}

    if (savedCode && savedCode !== 'undefined') {
        setCountryByCode(savedCode);
        countryDetectionDone = true;
        return;
    }

    // Priority 3: IP geolocation
    fetch('/ajax/geoinfo')
        .then(function(r) { return r.text(); })
        .then(function(raw) {
            var parts = raw.trim().toLowerCase().split('|');
            var code = parts[0];
            var source = parts[1] || 'unknown';
            if (code && code !== 'error') {
                try {
                    localStorage.setItem('newofficeplease_country_code', code);
                    localStorage.setItem('newofficeplease_ip_country', code);
                } catch(e) {}
                setCountryByCode(code);
            }
            countryDetectionDone = true;
        })
        .catch(function() { countryDetectionDone = true; });
}

export function initCountryDetection() {
    var pageId = document.body.id || '';

    // Pages where the search form is immediately visible: run on load
    if (pageId === 'listing' || pageId === 'home') {
        detectCountry();
        return;
    }

    // Detail/guide/inner pages: when search opens, prefer the entry's country
    // (user is already browsing in this country — search should stay there)
    var searchBtns = document.querySelectorAll('.search-btn');
    searchBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
            // Try entry's country first (from body data attribute)
            var entryCountry = (document.body.getAttribute('data-entry-country') || '').trim().toLowerCase();
            if (entryCountry) {
                setCountryByCode(entryCountry);
                countryDetectionDone = true;
                return;
            }
            // Fall back to normal detection chain
            detectCountry();
        }, { once: true });
    });
}

/**
 * Location searches ("Serviced Offices near ...") have no city category to
 * describe, so the heading type and the grey byline are worked out from the
 * office cards actually on the page. Labels match the server-side
 * other_office_types plugin tag used on city pages.
 */
const OFFICE_TYPE_LABELS = [
    ['serviced', 'Serviced offices', 'Serviced Offices'],
    ['cowork', 'Coworking spaces', 'Coworking Spaces'],
    ['virtual', 'Virtual offices', 'Virtual Offices'],
    ['sublet', 'Sublet offices', 'Sublet Offices'],
    ['leased', 'Leased spaces', 'Leased Spaces'],
    ['industrial', 'Industrial units', 'Industrial Units'],
    ['workshop', 'Industrial units', 'Industrial Units'],
    ['data cent', 'Data centres', 'Data Centres'],
    ['meeting', 'Meeting rooms', 'Meeting Rooms'],
];

function officeTypeLabel(rawType) {
    const type = rawType.trim().toLowerCase();
    if (!type) {
        return null;
    }
    const match = OFFICE_TYPE_LABELS.find(([needle]) => type.includes(needle));
    return match ? { byline: match[1], heading: match[2] } : null;
}

export function initListingTypeHeading() {
    const byline = document.querySelector('[data-listing-types-auto]');
    const headingLabel = document.querySelector('[data-listing-type-label]');
    if (!byline || !headingLabel) {
        return;
    }

    const cards = document.querySelectorAll('.office-card--listing[data-office-types]');
    if (!cards.length) {
        return;
    }

    // Count offices per type, so the heading names the most common one
    const counts = new Map();
    cards.forEach((card) => {
        const seen = new Set();
        card.dataset.officeTypes.split('|').forEach((rawType) => {
            const label = officeTypeLabel(rawType);
            if (label && !seen.has(label.heading)) {
                seen.add(label.heading);
                const entry = counts.get(label.heading) || { ...label, count: 0 };
                entry.count += 1;
                counts.set(label.heading, entry);
            }
        });
    });

    if (!counts.size) {
        return;
    }

    const ranked = [...counts.values()].sort((a, b) => b.count - a.count);
    const lead = counts.get('Serviced Offices') || ranked[0];
    headingLabel.textContent = lead.heading;

    const others = ranked.filter((entry) => entry !== lead).slice(0, 4).map((entry) => entry.byline);
    byline.textContent = others.length ? `Also available: ${others.join(' \u00b7 ')}` : '';
}

/**
 * Keeps the serviced and coworking prices side by side only while they fit:
 * smaller text if they nearly fit, otherwise just the serviced price.
 */
function fitIntroPrices(block) {
    const box = block?.querySelector('.listing-intro-prices');
    const items = box ? [...box.querySelectorAll('.listing-intro-price-item')] : [];
    if (items.length < 2) {
        return;
    }

    const lines = box.querySelectorAll('.listing-intro-price-label, .listing-intro-price-amount');
    const overflows = () => [...lines].some(line => line.scrollWidth > line.clientWidth + 1);

    const fit = () => {
        box.classList.remove('is-compact');
        items[1].hidden = false;
        if (!overflows()) return;
        box.classList.add('is-compact');
        if (!overflows()) return;
        box.classList.remove('is-compact');
        items[1].hidden = true;
    };

    fit();
    let frame = 0;
    window.addEventListener('resize', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(fit);
    });
}

/**
 * City introduction card in the listing sidebar (category_intro plugin tag).
 * The paragraphs stay in the HTML for search engines; this folds them behind a Read more toggle.
 */
export function initListingIntro() {
    const block = document.querySelector('[data-listing-intro]');
    fitIntroPrices(block);
    const toggle = block?.querySelector('.listing-intro-toggle');
    const label = toggle?.querySelector('[data-listing-intro-label]');
    if (!block || !toggle || !label) {
        return;
    }

    const moreLabel = label.textContent;
    block.classList.add('is-collapsed');
    toggle.hidden = false;

    toggle.addEventListener('click', () => {
        const expanding = block.classList.contains('is-collapsed');
        block.classList.toggle('is-collapsed', !expanding);
        toggle.setAttribute('aria-expanded', String(expanding));
        label.textContent = expanding ? 'Show less' : moreLabel;
    });
}
